// User Type Synchronization Utility
// This utility ensures consistent user type handling across the application

/**
 * Maps database user_type values to display labels
 * @param {string} userType - The user_type from database
 * @returns {string} - Human readable label
 */
export const getUserTypeLabel = (userType) => {
  switch (userType) {
    case 'it_administrator':
      return 'IT Administrator'
    case 'government_employee':
      return 'Government Employee'
    case 'security_officer':
      return 'Security Officer'
    case 'department_head':
      return 'Department Head'
    case 'compliance_officer':
      return 'Compliance Officer'
    case 'dpo':
      return 'Data Protection Officer'
    case 'it':
      return 'IT Personnel'
    case 'admin':
      return 'Administrator'
    default:
      return 'User'
  }
}

/**
 * Maps user_type to appropriate color classes
 * @param {string} userType - The user_type from database
 * @returns {string} - CSS classes for styling
 */
export const getUserTypeColor = (userType) => {
  switch (userType) {
    case 'it_administrator':
      return 'bg-blue-100 text-blue-800'
    case 'government_employee':
      return 'bg-green-100 text-green-800'
    case 'security_officer':
      return 'bg-purple-100 text-purple-800'
    case 'department_head':
      return 'bg-indigo-100 text-indigo-800'
    case 'compliance_officer':
      return 'bg-teal-100 text-teal-800'
    case 'security_officer':
      return 'bg-red-100 text-red-800'
    case 'dpo':
      return 'bg-cyber-100 text-cyber-800'
    case 'it':
      return 'bg-success-100 text-success-800'
    case 'admin':
      return 'bg-security-100 text-security-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

/**
 * Maps user_type to border color classes (for UserInfo component)
 * @param {string} userType - The user_type from database
 * @returns {string} - CSS classes for border styling
 */
export const getUserTypeBorderColor = (userType) => {
  switch (userType) {
    case 'it_administrator':
      return 'text-blue-600 bg-blue-50 border-blue-200'
    case 'government_employee':
      return 'text-green-600 bg-green-50 border-green-200'
    case 'security_officer':
      return 'text-purple-600 bg-purple-50 border-purple-200'
    case 'department_head':
      return 'text-indigo-600 bg-indigo-50 border-indigo-200'
    case 'compliance_officer':
      return 'text-teal-600 bg-teal-50 border-teal-200'
    case 'admin':
      return 'text-red-600 bg-red-50 border-red-200'
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200'
  }
}

/**
 * Checks if user has administrative privileges
 * @param {string} userType - The user_type from database
 * @returns {boolean} - True if user has admin privileges
 */
export const isAdminUser = (userType) => {
  return ['it_administrator', 'admin', 'department_head'].includes(userType)
}

/**
 * Checks if user is a government employee
 * @param {string} userType - The user_type from database
 * @returns {boolean} - True if user is a government employee
 */
export const isGovernmentEmployee = (userType) => {
  return userType === 'government_employee'
}

/**
 * Checks if user has security-related role
 * @param {string} userType - The user_type from database
 * @returns {boolean} - True if user has security role
 */
export const isSecurityRole = (userType) => {
  return ['security_officer'].includes(userType)
}

/**
 * Checks if user has compliance-related role
 * @param {string} userType - The user_type from database
 * @returns {boolean} - True if user has compliance role
 */
export const isComplianceRole = (userType) => {
  return ['compliance_officer', 'dpo'].includes(userType)
}

/**
 * Normalizes user type from various sources
 * @param {Object} profile - User profile from database
 * @param {Object} userContext - User context state
 * @returns {string} - Normalized user type
 */
export const getNormalizedUserType = (profile, userContext) => {
  // Priority: profile.user_type > userContext.userType > default
  return profile?.user_type || userContext?.userType || 'user'
}

/**
 * Validates if user type is valid
 * @param {string} userType - The user_type to validate
 * @returns {boolean} - True if valid user type
 */
export const isValidUserType = (userType) => {
  const validTypes = [
    'it_administrator',
    'government_employee',
    'cybersecurity_specialist',
    'department_head',
    'compliance_officer',
    'security_officer',
    'dpo',
    'it',
    'admin'
  ]
  return validTypes.includes(userType)
}

/**
 * Gets user permissions based on user type
 * @param {string} userType - The user_type from database
 * @returns {Object} - Permission object
 */
export const getUserPermissions = (userType) => {
  const basePermissions = {
    canView: true,
    canDownload: true,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canManageUsers: false,
    canAccessAdmin: false
  }

  switch (userType) {
    case 'it_administrator':
    case 'admin':
      return {
        ...basePermissions,
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canManageUsers: true,
        canAccessAdmin: true
      }
    
    case 'department_head':
      return {
        ...basePermissions,
        canCreate: true,
        canEdit: true,
        canAccessAdmin: true
      }
    
    case 'cybersecurity_specialist':
    case 'security_officer':
      return {
        ...basePermissions,
        canCreate: true,
        canEdit: true
      }
    
    case 'compliance_officer':
    case 'dpo':
      return {
        ...basePermissions,
        canCreate: true
      }
    
    default:
      return basePermissions
  }
}